<script setup>
  import { computed, watchEffect } from 'vue';
  import { useSpotStore } from '@/stores/SpotStore';
  import { useUserStore } from '@/stores/UserStore';
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import ToolbarHeaderActionListBase from "@/components/ToolbarHeaderActionListBase.vue";
  import FavoriteSpotUserTip from "@/components/FavoriteSpotUserTip.vue";
  import Icon from "@/components/icons/Icon.vue";
  import useSettingsGlobals from '@/composables/useSettingsGlobals';

  const { $WEB_VIEW, $SHOW_WIDGETS } = useSettingsGlobals();     

  const emit = defineEmits(['update:showProfileModal', 'showAlertManageModal']);

  const props = defineProps({
    buttons: {
      type: Array,
    }
  });

  const spotStore = useSpotStore();
  const userStore = useUserStore();

  const isDesktop = useMediaQuery('(min-width: 840px)');

  const showFavoriteButton = computed(() => props.buttons.includes('favorite') && userStore.memberLevel > 0);
  const showMapButton = computed(() => props.buttons.includes('map'));
  const showCompleteReportButton = computed(() => props.buttons.includes('complete-report'));
  const showAlertsButton = computed(() => props.buttons.includes('alerts') && userStore.memberLevel > 0);
  const showWidgetsButton = computed(() => props.buttons.includes('widgets') && $SHOW_WIDGETS);
  const showUserTip = computed(() => userStore.memberLevel > 0 && isDesktop.value && !$WEB_VIEW);


  const completeReportUrl = computed(() => {
    return spotStore.spot.spot_id
      ? `../spot/${spotStore.spot.spot_id}`
      : spotStore.spot.fav_spot_id
      ? `../favspot/${spotStore.spot.fav_spot_id}`
      : `en-us/Search/SpotInfo.aspx?latlon=${spotStore.lat},${spotStore.lon}`;
  })

  function handleShowAlertManageModal() {
    emit('showAlertManageModal', true);
  }

  function handleShowProfileModal() {
    emit('update:showProfileModal', true);
  }
</script>

<template>
  <ToolbarHeaderActionListBase>
    <template #default="{ classes }">
      <li v-if="showFavoriteButton" :class="classes.item">
        <button
          @click="handleShowProfileModal"
          type="button"
          :class="classes.button"
        >
          <Icon v-if="spotStore.isSpotFavorited" name="star-solid" :class="classes.icon" data-selected />
          <Icon v-else name="star" :class="classes.icon" />
          <span :class="classes.label">Favorite</span>
        </button>
        <FavoriteSpotUserTip v-if="showUserTip" />
      </li>
      <li
        v-if="showMapButton"
        :class="classes.item"
      >
        <a
          :href="`/map#${spotStore.lat},${spotStore.lon},11`"
          :class="classes.button"
        >
          <Icon name="map-location" :class="classes.icon" style="width: 20px;" />
          <span :class="classes.label">Map</span>
        </a>
      </li>
      <li
        v-if="showCompleteReportButton"
        :class="classes.item"
      >
        <a
          :href="completeReportUrl"
          :class="classes.button"
        >
          <Icon name="complete-report" :class="classes.icon" style="width: 20px;" />
          <span :class="classes.label">Report</span>
        </a>
      </li>
      <li
        v-if="showAlertsButton"
        :class="classes.item"
      >
        <button
          @click.prevent="handleShowAlertManageModal"
          :class="classes.button"
        >
          <span :class="classes.iconWrapper">
            <Icon name="bell" :class="classes.icon" />
            <span :class="classes.badge"><Icon name="plus" /></span>
          </span>
          <span :class="classes.label">Alert</span>
        </button>
      </li>
      <li
        v-if="showWidgetsButton"
        :class="classes.item"
      >
        <a
          :href="`/widget/${spotStore.spot.spot_id}`"
          :class="classes.button"
        >
          <Icon name="grid" :class="classes.icon" />
          <span :class="classes.label">Widgets</span>
        </a>
      </li>
    </template>
  </ToolbarHeaderActionListBase>
</template>